import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { AppVersionCheck } from '@sdir/utilities/lib/components';
import { AlertBox } from '@sdir/sds';
import Routes from './components/Routes';
import { AppLayout } from './components/Templates';
import { useCurrentUserInfo } from './services/hooks/useCurrentUserInfo';

const baseUrl = process.env.REACT_APP_VERCHECK_URL || window.location.href.split('#')[0];
const versionCheckInterval = process.env.REACT_APP_VERCHECK_INTERVAL
  ? +process.env.REACT_APP_VERCHECK_INTERVAL
  : null;

type AppProps = {
  currentLanguage: string;
  onChangeLanguage: (l: string, skipRefresh?: boolean) => void;
};

const App: React.FC<AppProps> = ({ currentLanguage, onChangeLanguage }) => {
  const intl = useIntl();
  const {
    userInfo: { userLoaded, isOrganization, isPerson },
  } = useCurrentUserInfo();

  const generalError = useMemo(() => {
    if (userLoaded && (isOrganization || !isPerson)) {
      return (
        <Wrapper>
          <AlertBox
            type="info"
            title={intl.formatMessage({ id: 'general.error.title' })}
            text={intl.formatMessage({ id: 'login.org.error' })}
          />
        </Wrapper>
      );
    }
    return undefined;
  }, [userLoaded, isOrganization, isPerson]);

  useEffect(() => {
    if (document?.documentElement?.lang) {
      document.documentElement.lang = currentLanguage;
    }
  }, [currentLanguage]);

  return (
    <AppLayout onChangeLanguage={onChangeLanguage}>
      {versionCheckInterval && (
        <AppVersionCheck baseUrl={baseUrl} checkIntervalMs={versionCheckInterval} />
      )}
      {generalError || (
        <Routes currentLanguage={currentLanguage} onChangeLanguage={onChangeLanguage} />
      )}
    </AppLayout>
  );
};

export default App;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;
